"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
import { MapPin, Mail, Phone, Clock, ExternalLink, Loader2 } from "lucide-react";
const PRACTICE_AREAS = ["Blenheim", "Tricolor", "Ruby", "Black & Tan", "General Inquiry"];
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="224539" className="flex flex-col items-center justify-center rounded-xl border border-border bg-accent/30 p-8 text-center">
        <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <svg className="size-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="mb-2 font-default text-xl font-semibold text-foreground">
          Application Received
        </h3>
        <p className="mb-6 text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="font-default">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="name" rules={{
      required: "Your full name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-foreground">Full Name</FormLabel>
            <FormControl>
              <Input placeholder="Elizabeth Bennet" className="border-0 border-b border-input rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-primary transition-colors" {...field} />
            </FormControl>
            <FormDescription className="font-sans text-xs">Please provide your legal name.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="elizabeth@estate.com" className="border-0 border-b border-input rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-primary transition-colors" {...field} />
              </FormControl>
              <FormDescription className="font-sans text-xs">For our formal correspondence.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" className="border-0 border-b border-input rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-primary transition-colors" {...field} />
              </FormControl>
              <FormDescription className="font-sans text-xs">
                For urgent updates regarding litters.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="practice_area" rules={{
      required: "Please select a coat preference"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-foreground">Preferred Coat Color</FormLabel>
            <FormControl>
              <select className="border-0 border-b border-input focus-visible:border-primary focus-visible:ring-0 dark:bg-transparent flex h-9 w-full min-w-0 rounded-none bg-transparent px-3 py-1 text-base transition-colors outline-none disabled:pointer-events-none disabled:opacity-50 md:text-sm font-sans" {...field}>
                <option value="" disabled>
                  Select your preference
                </option>
                {PRACTICE_AREAS.map((area, i) => <option key={area} value={area} data-index={i}>
                    {area}
                  </option>)}
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Please tell us about your home"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-foreground">Your Home Environment</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your lifestyle and experience with the breed..." className="min-h-[120px] resize-y border-0 border-b border-input rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-primary transition-colors" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4">
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/20 rounded-full transition-all md:w-auto md:px-12 font-default" size="lg">
        {isSubmitting ? <>
            <Loader2 className="mr-2 size-5 animate-spin" />
            Processing...
          </> : "Submit Application"}
      </Button>
    </Form>;
}
export default function FaleComEspecialistas() {
  const contactBlocks = [{
    icon: <Phone className="size-6 text-primary" />,
    title: "Concierge Line",
    content: "(555) 123-4567",
    action: <Link to="#" newTab className="mt-2 inline-flex items-center text-sm font-medium text-primary hover:underline font-sans">
          Call our estate <ExternalLink className="ml-1 size-3" />
        </Link>
  }, {
    icon: <Mail className="size-6 text-primary" />,
    title: "Inquiries",
    content: "hello@royal-cavaliers.com",
    action: <Link to="mailto:hello@royal-cavaliers.com" className="mt-2 inline-flex items-center text-sm font-medium text-primary hover:underline font-sans">
          Send an email <ExternalLink className="ml-1 size-3" />
        </Link>
  }, {
    icon: <Clock className="size-6 text-primary" />,
    title: "Consultation Hours",
    content: "Mon - Fri, 9am to 5pm EST",
    action: null
  }, {
    icon: <MapPin className="size-6 text-primary" />,
    title: "The Estate",
    content: "Charleston, South Carolina",
    action: <Link to="#" newTab className="mt-3 inline-flex items-center justify-center rounded-full border border-border bg-background px-4 py-2 text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-accent font-sans">
          View Location
        </Link>
  }];
  return;
}